import { HStack, Modal, VStack } from '@musinsa/mwms-ds'
import { Icon as LayerIcon } from '@musinsa/pbo-assets'
import Text from '@/components/content/Text'
import { Radio } from '@/components/content/Radio'
import styled from 'styled-components'
import SelectBox from '@/components/content/SelectBox'
import { useState } from 'react'
import { AxiosError } from 'axios'
import MAlert from '@/utils/modal/MAlert'
import MToast from '@/utils/modal/MToast'
import { Utils } from '@/utils/Utils'
import { getShipperScopeErrorMessage } from '@/utils/shipperScope'
import { colors, colorSystem } from '@/styles/theme'
import { useUpsertGlobalCuttime } from '@/services/outboundSchedule2/useOutboundScheduleQueries'
import { useCutOffDate } from '../../hooks/useCutOffDate'
import { tokens } from '@musinsa/layer-tokens/desktop'
import dayjs from 'dayjs'

interface Props {
  endTime?: string | null
  isEnabled: boolean
  onClose: () => void
}

// 00 ~ 23
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0')
  return { label: hour, value: hour }
})

// 5분 단위
const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const minute = (i * 5).toString().padStart(2, '0')
  return { label: minute, value: minute }
})

const splitTime = (time?: string | null) => {
  if (!time) return { hour: '', minute: '' }
  const [hour = '', minute = ''] = time.split(':')
  return { hour, minute }
}

export function SetCutOperationTimeModal({ endTime, isEnabled, onClose }: Props) {
  const initialEnd = splitTime(endTime)

  const [endHour, setEndHour] = useState<string>(initialEnd.hour)
  const [endMinute, setEndMinute] = useState<string>(initialEnd.minute)
  const [enabled, setEnabled] = useState(isEnabled)

  const { cutOffDate, shipperCode } = useCutOffDate()
  const upsertGlobalCuttime = useUpsertGlobalCuttime()

  const end = endHour && endMinute ? `${endHour}:${endMinute}` : ''

  // 현재 시간 + 5분 이후만 설정 가능 (과거/현재/근접 5분 이내 모두 불가)
  const MIN_LEAD_MIN = 5
  const minSettableTime = dayjs().add(MIN_LEAD_MIN, 'minute')
  const isFutureEnough = !!end && dayjs(`${cutOffDate} ${end}`).isAfter(minSettableTime)
  const showError = !!end && !isFutureEnough
  // 미사용은 cutTime을 null로 보내므로 시간 입력 불요. 사용일 때만 미래 시간 검증.
  const isValid = !enabled || (!!end && isFutureEnough)

  const handleSubmit = async () => {
    try {
      await upsertGlobalCuttime.mutateAsync({
        shipperCode,
        cutType: 'B2C',
        cutTime: enabled ? `${end}:00` : null,
        isEnabled: enabled,
        applyDate: cutOffDate,
      })
      MToast.show('오늘 무배당발 출고마감 시간이 저장되었습니다.')
      onClose()
    } catch (e) {
      MAlert.show(getShipperScopeErrorMessage(e) ?? Utils.getApiErrMessage(e as AxiosError<ApiResponseV2>, '무배당발 출고마감 시간 설정에 실패했습니다.'))
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={<Text kind="Headline_20_bold">무배당발 출고마감 시간 관리</Text>}
      buttons={[
        { kind: 'secondary', content: '취소', onClick: onClose },
        { kind: 'primary_light', content: '저장', onClick: handleSubmit, disabled: !isValid },
      ]}
    >
      <DescriptionBox gap={8} align="flex-start" style={{ marginBottom: 24 }}>
        <IconWrap>
          <LayerIcon name="ic-infoCircle" size={18} filled color={colors.gray500} />
        </IconWrap>
        <Text kind="Body_14_regular" color={tokens.fg} style={{ fontFamily: "'Pretendard', sans-serif" }}>
          출고 마감시간에 맞춰 대기중인 주문 모두 일괄 지시 처리 됩니다. 지연상황을 대비하여 15분 정도 여유 시간을 두고 설정해주세요.
        </Text>
      </DescriptionBox>

      <VStack gap={16}>
        <HStack gap={16} align="flex-start">
          <Label kind="Body_14_medium">사용여부</Label>
          <VStack gap={4}>
            <HStack gap={32}>
              <Radio value="true" checked={enabled} label="사용" onChange={() => setEnabled(true)} />
              <Radio value="false" checked={!enabled} label="미사용" onChange={() => setEnabled(false)} />
            </HStack>
          </VStack>
        </HStack>

        <HStack gap={16} align="flex-start">
          <Label kind="Body_14_medium">출고 마감시간</Label>
          <VStack gap={4}>
            <HStack gap={8} align="center">
              <SelectWrap>
                <SelectBox size="large" block options={HOUR_OPTIONS} value={endHour} placeholder="시" onChange={setEndHour} disabled={!enabled} />
              </SelectWrap>
              <Text kind="Body_14_regular">:</Text>
              <SelectWrap>
                <SelectBox
                  size="large"
                  block
                  options={MINUTE_OPTIONS}
                  value={endMinute}
                  placeholder="분"
                  onChange={setEndMinute}
                  disabled={!enabled}
                />
              </SelectWrap>
            </HStack>
            {enabled && showError && (
              <Text kind="Body_12_medium" color={colorSystem.text.common.error}>
                현재 시간으로부터 {MIN_LEAD_MIN}분 이후 시간으로 설정해 주세요.
              </Text>
            )}
          </VStack>
        </HStack>
      </VStack>
    </Modal>
  )
}

const Label = styled(Text)`
  min-width: 110px;
  padding: 4px 0;
`

const SelectWrap = styled.div`
  width: 90px;
`

const DescriptionBox = styled(HStack)`
  padding: 16px 12px;
  border: 1px solid #ebebeb;
  border-radius: 4px;
  background-color: rgba(224, 224, 224, 0.2);
`

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  height: 20px;
  flex-shrink: 0;
`
